import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Wheat, Thermometer, Calendar, Droplets, AlertCircle, CheckCircle } from 'lucide-react';

export function StarterGuide() {
  const [expandedSection, setExpandedSection] = useState<string | null>('overview');

  const toggleSection = (section: string) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  const Section = ({ id, title, children }: { id: string; title: string; children: React.ReactNode }) => (
    <div className="border border-white/10 rounded-xl overflow-hidden mb-4">
      <button
        onClick={() => toggleSection(id)}
        className="w-full flex items-center justify-between p-4 bg-white/5 hover:bg-white/10 transition-colors"
      >
        <h3 className="text-lg text-white font-medium">{title}</h3>
        {expandedSection === id ? (
          <ChevronUp className="w-5 h-5 text-white/70" />
        ) : (
          <ChevronDown className="w-5 h-5 text-white/70" />
        )}
      </button>
      {expandedSection === id && (
        <div className="p-4 bg-white/[0.02] space-y-4">
          {children}
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-3xl text-white font-brand font-light italic mb-2">Creating & Managing Starters</h2>
        <p className="text-white/80">
          Learn how to create, maintain, and optimize your sourdough starters for perfect bread every time.
        </p>
      </div>

      <Section id="overview" title="What is a Sourdough Starter?">
        <p className="text-white/80">
          A sourdough starter is a culture of wild yeast and beneficial bacteria living in a mixture of flour and water.
          It's the heart of sourdough baking, providing natural leavening and distinctive flavor to your bread.
        </p>
        <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4 mt-4">
          <div className="flex items-start gap-3">
            <Wheat className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-white/90 font-medium mb-1">Why Track Your Starter?</p>
              <p className="text-white/70 text-sm">
                Breadlify helps you monitor your starter's health, track feeding schedules, and get timely reminders
                so your starter is always ready when you need it.
              </p>
            </div>
          </div>
        </div>
      </Section>

      <Section id="creating" title="Creating a New Starter">
        <p className="text-white/80 mb-4">
          To create a new starter, click the "+" button in the Starter tab. Here's what each field means:
        </p>

        <div className="space-y-4">
          <div className="bg-white/5 rounded-lg p-4">
            <h4 className="text-white font-medium mb-2 flex items-center gap-2">
              <span className="w-6 h-6 bg-amber-500/20 rounded-full flex items-center justify-center text-xs text-amber-400">1</span>
              Name
            </h4>
            <p className="text-white/70 text-sm mb-2">
              Give your starter a memorable name. This helps you identify it if you maintain multiple starters.
            </p>
            <p className="text-white/60 text-xs italic">
              Example: "Whole Wheat Starter", "My First Starter", "Rye Powerhouse"
            </p>
          </div>

          <div className="bg-white/5 rounded-lg p-4">
            <h4 className="text-white font-medium mb-2 flex items-center gap-2">
              <span className="w-6 h-6 bg-amber-500/20 rounded-full flex items-center justify-center text-xs text-amber-400">2</span>
              Texture
            </h4>
            <p className="text-white/70 text-sm mb-3">
              Choose between liquid and stiff consistency. This affects hydration and fermentation speed:
            </p>
            <ul className="space-y-2 text-sm">
              <li className="flex items-start gap-2 text-white/70">
                <Droplets className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" />
                <div>
                  <span className="text-white font-medium">Liquid (100% hydration):</span> Equal parts flour and water by weight.
                  More active fermentation, easier to mix. Best for beginners.
                </div>
              </li>
              <li className="flex items-start gap-2 text-white/70">
                <Wheat className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                <div>
                  <span className="text-white font-medium">Stiff (60% hydration):</span> Less water, firmer consistency.
                  Produces more acetic acid, giving tangier flavor. Requires more kneading.
                </div>
              </li>
            </ul>
          </div>

          <div className="bg-white/5 rounded-lg p-4">
            <h4 className="text-white font-medium mb-2 flex items-center gap-2">
              <span className="w-6 h-6 bg-amber-500/20 rounded-full flex items-center justify-center text-xs text-amber-400">3</span>
              Feeding Ratio
            </h4>
            <p className="text-white/70 text-sm mb-3">
              The ratio of starter:flour:water used when feeding. Common ratios:
            </p>
            <div className="space-y-2 text-sm">
              <div className="bg-white/5 rounded p-3">
                <p className="text-white font-medium">1:1:1 - Maintenance Feeding</p>
                <p className="text-white/60 text-xs mt-1">
                  Equal parts. Best for daily feeding and keeping starter healthy without too much growth.
                </p>
              </div>
              <div className="bg-white/5 rounded p-3">
                <p className="text-white font-medium">1:2:2 - Standard Build</p>
                <p className="text-white/60 text-xs mt-1">
                  Moderate growth. Good for regular baking schedule, peaks in 4-8 hours at room temp.
                </p>
              </div>
              <div className="bg-white/5 rounded p-3">
                <p className="text-white font-medium">1:5:5 or 1:10:10 - Long Fermentation</p>
                <p className="text-white/60 text-xs mt-1">
                  Lots of food for the starter. Takes 8-12+ hours to peak. Perfect for overnight builds.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white/5 rounded-lg p-4">
            <h4 className="text-white font-medium mb-2 flex items-center gap-2">
              <span className="w-6 h-6 bg-amber-500/20 rounded-full flex items-center justify-center text-xs text-amber-400">4</span>
              Flour Type
            </h4>
            <p className="text-white/70 text-sm mb-3">
              The type of flour you use to feed your starter. Common options:
            </p>
            <ul className="space-y-2 text-sm text-white/70">
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                <div>
                  <span className="text-white font-medium">All-Purpose:</span> Consistent, reliable, good for beginners
                </div>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                <div>
                  <span className="text-white font-medium">Whole Wheat:</span> More nutrients, very active fermentation
                </div>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                <div>
                  <span className="text-white font-medium">Rye:</span> Extremely active, strong flavor, easier to maintain
                </div>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                <div>
                  <span className="text-white font-medium">Bread Flour:</span> Higher protein, good rise, neutral flavor
                </div>
              </li>
            </ul>
          </div>

          <div className="bg-white/5 rounded-lg p-4">
            <h4 className="text-white font-medium mb-2 flex items-center gap-2">
              <span className="w-6 h-6 bg-amber-500/20 rounded-full flex items-center justify-center text-xs text-amber-400">5</span>
              Management Strategy
            </h4>
            <p className="text-white/70 text-sm mb-3">
              Choose how you want to maintain your starter:
            </p>
            <div className="space-y-2 text-sm">
              <div className="bg-white/5 rounded p-3">
                <p className="text-white font-medium">Normal - Regular Feeding</p>
                <p className="text-white/60 text-xs mt-1">
                  Standard maintenance. Feed when starter shows signs of hunger (every 12-24 hours at room temp).
                </p>
              </div>
              <div className="bg-white/5 rounded p-3">
                <p className="text-white font-medium">Overboost - Intensive Care</p>
                <p className="text-white/60 text-xs mt-1">
                  More frequent feedings (every 8-12 hours) to strengthen a weak starter. Must be kept at room temperature.
                  Great for reviving neglected starters.
                </p>
              </div>
              <div className="bg-white/5 rounded p-3">
                <p className="text-white font-medium">Maintenance - Minimal Care</p>
                <p className="text-white/60 text-xs mt-1">
                  For bakers who bake less frequently. Can be stored in the fridge and fed weekly.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white/5 rounded-lg p-4">
            <h4 className="text-white font-medium mb-2 flex items-center gap-2">
              <span className="w-6 h-6 bg-amber-500/20 rounded-full flex items-center justify-center text-xs text-amber-400">6</span>
              Storage Location
            </h4>
            <p className="text-white/70 text-sm mb-3">
              Where you keep your starter affects its fermentation speed:
            </p>
            <ul className="space-y-2 text-sm text-white/70">
              <li className="flex items-start gap-2">
                <Thermometer className="w-4 h-4 text-orange-400 flex-shrink-0 mt-0.5" />
                <div>
                  <span className="text-white font-medium">Room Temperature:</span> Active fermentation, needs daily feeding.
                  Specify your room temp for accurate feeding schedules (typically 18-24°C).
                </div>
              </li>
              <li className="flex items-start gap-2">
                <Thermometer className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" />
                <div>
                  <span className="text-white font-medium">Refrigerator:</span> Slows fermentation dramatically. Can be fed
                  weekly. Take out and feed 8-12 hours before baking.
                </div>
              </li>
            </ul>
            <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3 mt-3">
              <p className="text-white/80 text-xs">
                <AlertCircle className="w-3 h-3 inline mr-1" />
                Overboost strategy requires room temperature storage for best results.
              </p>
            </div>
          </div>

          <div className="bg-white/5 rounded-lg p-4">
            <h4 className="text-white font-medium mb-2 flex items-center gap-2">
              <span className="w-6 h-6 bg-amber-500/20 rounded-full flex items-center justify-center text-xs text-amber-400">7</span>
              Last Fed Date & Time
            </h4>
            <p className="text-white/70 text-sm">
              When you last fed your starter. This is crucial for Breadlify to calculate:
            </p>
            <ul className="space-y-1 mt-2 text-sm text-white/60 ml-4">
              <li>• When your starter needs its next feeding</li>
              <li>• Feeding reminders and notifications</li>
              <li>• Whether your starter is healthy and ready to use</li>
              <li>• Optimal timing for baking sessions</li>
            </ul>
          </div>
        </div>
      </Section>

      <Section id="health" title="Understanding Starter Health">
        <p className="text-white/80 mb-4">
          Breadlify monitors your starter's health and shows visual indicators:
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-3 h-3 bg-green-400 rounded-full"></div>
              <h4 className="text-white font-medium">Healthy (0-10% overdue)</h4>
            </div>
            <p className="text-white/70 text-sm">
              Your starter is well-fed and ready to use for baking. No action needed.
            </p>
          </div>

          <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-3 h-3 bg-yellow-400 rounded-full"></div>
              <h4 className="text-white font-medium">Needs Feeding (10-30% overdue)</h4>
            </div>
            <p className="text-white/70 text-sm">
              Your starter is getting hungry. Feed it soon to maintain its health and activity.
            </p>
          </div>

          <div className="bg-orange-500/10 border border-orange-500/20 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-3 h-3 bg-orange-400 rounded-full"></div>
              <h4 className="text-white font-medium">Urgent (30-50% overdue)</h4>
            </div>
            <p className="text-white/70 text-sm">
              Your starter is very hungry. Feed it as soon as possible to prevent weakening.
            </p>
          </div>

          <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-3 h-3 bg-red-400 rounded-full"></div>
              <h4 className="text-white font-medium">Critical (50%+ overdue)</h4>
            </div>
            <p className="text-white/70 text-sm">
              Your starter needs immediate attention. Feed multiple times to revive it.
            </p>
          </div>
        </div>
      </Section>

      <Section id="feeding" title="Feeding Your Starter">
        <p className="text-white/80 mb-4">
          To feed your starter, click on it in the Starter tab and select "Feed Now":
        </p>
        <ol className="space-y-3 text-white/70 text-sm">
          <li className="flex items-start gap-3">
            <span className="text-amber-400 font-bold text-lg">1.</span>
            <span>Discard all but the amount specified by your ratio (e.g., for 1:2:2, keep 50g)</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="text-amber-400 font-bold text-lg">2.</span>
            <span>Add the calculated amounts of flour and water</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="text-amber-400 font-bold text-lg">3.</span>
            <span>Mix thoroughly until no dry flour remains</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="text-amber-400 font-bold text-lg">4.</span>
            <span>Mark the level with a rubber band to track rise</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="text-amber-400 font-bold text-lg">5.</span>
            <span>Record the feeding in Breadlify to update your schedule</span>
          </li>
        </ol>
      </Section>

      <Section id="notifications" title="Feeding Notifications">
        <p className="text-white/80 mb-4">
          Breadlify sends automatic notifications when your starter needs feeding:
        </p>
        <ul className="space-y-2 text-white/70 text-sm">
          <li className="flex items-start gap-2">
            <Calendar className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
            <span>Email reminders when feeding time approaches</span>
          </li>
          <li className="flex items-start gap-2">
            <Calendar className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
            <span>In-app notifications appear in the notification bell</span>
          </li>
          <li className="flex items-start gap-2">
            <Calendar className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
            <span>Notifications based on your feeding ratio and storage location</span>
          </li>
        </ul>
        <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4 mt-4">
          <p className="text-white/80 text-sm">
            Configure notification preferences in Settings → Notifications to customize which alerts you receive.
          </p>
        </div>
      </Section>

      <Section id="tips" title="Pro Tips">
        <ul className="space-y-3 text-white/70 text-sm">
          <li className="flex items-start gap-3 bg-white/5 rounded-lg p-3">
            <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
            <div>
              <span className="text-white font-medium block mb-1">Use a scale</span>
              Weight measurements are more accurate than volume. Invest in a digital kitchen scale.
            </div>
          </li>
          <li className="flex items-start gap-3 bg-white/5 rounded-lg p-3">
            <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
            <div>
              <span className="text-white font-medium block mb-1">Mark your favorite</span>
              Set one starter as your favorite so it appears on your dashboard for quick access.
            </div>
          </li>
          <li className="flex items-start gap-3 bg-white/5 rounded-lg p-3">
            <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
            <div>
              <span className="text-white font-medium block mb-1">Don't throw away discard</span>
              Save your discard for pancakes, crackers, or other recipes. It's too delicious to waste!
            </div>
          </li>
          <li className="flex items-start gap-3 bg-white/5 rounded-lg p-3">
            <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
            <div>
              <span className="text-white font-medium block mb-1">Temperature matters</span>
              Warmer = faster fermentation. Cooler = slower. Adjust feeding times based on your kitchen temperature.
            </div>
          </li>
        </ul>
      </Section>
    </div>
  );
}
